import React, { useMemo, useState } from 'react';
import { Box, Typography } from '@mui/material';
import { useDrag } from 'react-dnd';
import { ImageMetadata } from '../../types/ui';
import MultiKebab from '../../ui/organisms/MenuItems/MultiKebab';
import CustomTooltip from '../../ui/atoms/CustomTooltip';
import IconCopy from '../../assets/icons/IconCopy';
import IconRemove from '../../assets/icons/IconRemove';
import { useDispatch } from 'react-redux';
import useMediaLabels from '../MainComponent/hooks/useMediaLabels';
import { addMediaLabelToImage } from '../../redux/reducer/coreScriptReducer';
import CustomCheckbox from '../../ui/atoms/checkbox';
import { useSelector } from 'react-redux';
import { debounce } from '../../utils';
import { RootState, useAppSelector, AppDispatch } from '../../redux/Store';
import useImages from '../MainComponent/hooks/useImages';
const ItemTypes = {
  IMAGE: 'image',
};

type DraggableImageProps = {
  title?: string;
  expanded?: boolean;
  image: ImageMetadata;
  index: number;
  isSelected?: boolean;
  onItemClick?: () => void;
  showCheckbox?: boolean;
  onDoubleClickView?: () => void;
  disabled?: boolean;
  selectedGuids?: string[]; // <-- add this prop
  unlabeledImages?: ImageMetadata[]; // <-- add this prop
};

const DraggableImage: React.FC<DraggableImageProps> = ({
  title = '',
  expanded,
  image,
  index,
  isSelected = false,
  onItemClick,
  onDoubleClickView,
  selectedGuids = [],
  unlabeledImages = [],
}) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const menuOptions = useLabelMenuOptions(image, () => {
    setIsMenuOpen(false);
  });
  const { useImage403ErrorHandler } = useImages();
  const isReadOnly = useSelector(
    (state: RootState) => state.contextId.isReadOnly
  );

  // UseDrag only when isReadOnly is false
  // Multi-drag: if this image is selected, drag all selectedGuids, else just itself
  const dragImages =
    isSelected && selectedGuids.length > 1
      ? unlabeledImages.filter((img) => selectedGuids.includes(img.guid))
      : [image];

  const [{ isDragging }, drag] = useDrag(
    () => ({
      type: ItemTypes.IMAGE,
      item: {
        images: dragImages,
        indices: dragImages.map((img) => img.index ?? -1),
      },
      collect: (monitor) => ({
        isDragging: !!monitor.isDragging(),
      }),
    }),
    [image, index, isSelected, selectedGuids, unlabeledImages]
  );

  return (
    <Box
      ref={isReadOnly ? null : drag} // Prevents dragging when isReadOnly is true
      sx={{
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
        width: expanded ? '200px' : '100px',
        padding: 0,
        alignItems: 'center',
        gap: '8px',
        flexShrink: 0,
        borderRadius: expanded ? '8px' : '10px',
        border: '1px solid #D8E0E5',
        background: '#FFF',
        overflow: 'hidden',
        transition: 'width 0.3s ease-in-out', // Smooth transition for expanded width
        '@media (max-width: 600px)': {
          // Adjustments for smaller screens
          width: '100%',
          padding: '4px',
        },
        cursor: isReadOnly ? 'not-allowed' : 'grab',
      }}
    >
      <Box
        sx={{
          width: '100%',
          height: 0,
          paddingTop: '75%', // Fixed aspect ratio (4:3)
          backgroundColor: '#F4F4F4', // Placeholder for no image
          position: 'relative',
        }}
      >
        <img
          src={image?.webFileName || ''}
          onError={useImage403ErrorHandler}
          onDoubleClick={(e) => {
            e.stopPropagation();
            onDoubleClickView && onDoubleClickView();
          }}
          alt={`Uploaded file ${index + 1}`}
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            objectFit: 'cover',
          }}
        />
      </Box>
      <CustomCheckbox
        checked={isSelected}
        onChange={onItemClick}
        sx={{ position: 'absolute', top: '0px', left: '0px', padding: 0 }}
      />
      {/* Label and Kebab Menu (Horizontally Aligned) */}
      <Box
        sx={{
          width: '100%',
          padding: '8px',
          display: isReadOnly ? 'none' : 'flex',
          alignItems: 'center',
          justifyContent: 'space-between', // Justifies space between label and kebab menu
          marginTop: '-8px',
          height: '50px',
        }}
      >
        <Typography
          sx={{
            color: '#343330',
            fontFamily: 'Montserrat',
            fontSize: expanded ? '12px' : '10px',
            fontWeight: 500,
            textTransform: 'capitalize',
            display: '-webkit-box',
            WebkitBoxOrient: 'vertical',
            WebkitLineClamp: 2, // Limit to 2 lines
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            lineHeight: 1.5,
            textAlign: 'left', // Align the text to the left
            flex: 1, // Ensures the label takes available space
          }}
        >
          <CustomTooltip label={title} placement='top' arrow>
            <span>{title}</span>
          </CustomTooltip>
        </Typography>
        <Box
          onClick={(e) => e.stopPropagation()}
          sx={{
            cursor: 'pointer',
          }}
        >
          <MultiKebab
            disabled={false}
            options={menuOptions}
            isMenuOpen={isMenuOpen}
            setIsMenuOpen={(value: boolean) => {
              setIsMenuOpen(value);
            }}
          />
        </Box>
      </Box>
    </Box>
  );
};

const useLabelMenuOptions = (media: ImageMetadata, hideMenu?: () => void) => {
  const { processImageDeleteRequest } = useImages();
  const dispatch = useDispatch<AppDispatch>();
  const { uploadedSuccessfullyCurrentSession } = useAppSelector(
    (state) => state.gallery
  );
  const { mediaLabels, labels } = useMediaLabels();
  const handleAttachLabel = debounce((targetLabel: string[]) => {
    for (const label of targetLabel) {
      dispatch(
        addMediaLabelToImage({
          label: label,
          media: [{ Guid: media.guid, ThumbUri: media.webFileName }],
        })
      );
    }

    hideMenu?.();
  }, 50);

  const handleDeleteImage = debounce(() => {
    processImageDeleteRequest(media);
    hideMenu?.();
  }, 50);

  return useMemo(() => {
    const sortedLabels = labels.sort((a, b) => {
      const labelObjA = mediaLabels?.find((item) => item?.Label === a);
      const labelObjB = mediaLabels?.find((item) => item?.Label === b);
      const isResolvedA = labelObjA?.Complete;
      const isResolvedB = labelObjB?.Complete;
      return Number(isResolvedA) - Number(isResolvedB);
    });
    const isUploaded = uploadedSuccessfullyCurrentSession.includes(media.guid);

    const options: any = [
      {
        id: '1',
        label: 'Attach Label',
        subOptions: sortedLabels.map((label) => {
          const labelObj = mediaLabels.find((item) => item.Label === label);
          return {
            id: label,
            label: label.toLanguagePack(),
            subtitle: labelObj?.Complete
              ? 'Resolved'
              : `${labelObj?.Have}/${labelObj?.Min}`,
            required: labelObj?.Required,
            onClick: () => handleAttachLabel([label]),
          };
        }),
        prefixIcon: <IconCopy />,
        // multiselect: true,  onMultiselectDismiss: handleAttachLabel,
      },
    ];

    if (isUploaded) {
      options.push({
        id: '2',
        label: 'Delete Image',
        onClick: handleDeleteImage,
        prefixIcon: <IconRemove />,
      });
    }

    return options;
  }, [
    handleAttachLabel,
    handleDeleteImage,
    labels,
    media.guid,
    mediaLabels,
    uploadedSuccessfullyCurrentSession,
  ]);
};

export default React.memo(DraggableImage);
